/**
 * @component AdminPage
 * @description 인사말·연혁·뉴스·FAQ를 한곳에서 관리하는 관리자 콘솔입니다.
 *              로그인 여부에 따라 로그인 화면 또는 탭 편집 화면을 보여줍니다.
 * @usage App 라우팅에서 `/admin`(인사말), `/admin/history`, `/admin/news`, `/admin/faq` 경로일 때 렌더링됩니다.
 */

import { useEffect } from 'react';
import AdminLoginPanel from '../../components/admin/AdminLoginPanel';
import HistoryAdminPanel from './HistoryAdminPanel';
import NewsAdminPanel from './NewsAdminPanel';
import FaqAdminPanel from './FaqAdminPanel';
import ContentAdminPanel from './ContentAdminPanel';
import { useAdminSession } from '../../hooks/useAdminSession';

// [비즈니스] 탭을 URL로 구분해, 새로고침이나 뒤로가기를 해도 보고 있던 탭이 유지되게 합니다.
const TABS = [
  { id: 'content', label: '인사말', path: '/admin' },
  { id: 'history', label: '연혁', path: '/admin/history' },
  { id: 'news', label: '뉴스', path: '/admin/news' },
  { id: 'faq', label: 'FAQ', path: '/admin/faq' },
];

// [기능] 로그인 상태와 선택 탭에 맞는 관리자 화면을 표시합니다.
// [비즈니스] 라우트가 탭을 지정하지 않으면 /admin 과 같은 인사말 탭을 보여 줍니다.
export default function AdminPage({ activeTab = 'content', onNavigate }) {
  const { status, isAuthenticated, isSubmitting, error, login, logout, markUnauthorized, clearError } =
    useAdminSession();

  // [기술] 관리자 콘솔은 공개 페이지와 배경색이 달라, body에 클래스를 붙여 화면 전체 톤을 맞춥니다.
  useEffect(() => {
    document.body.classList.add('is-admin-console');
    return () => document.body.classList.remove('is-admin-console');
  }, []);

  // [비즈니스] 세션 확인이 끝나기 전에 로그인 화면을 먼저 보여 주면, 이미 로그인한 관리자에게
  //            로그인 폼이 잠깐 나타났다 사라지는 깜빡임이 생깁니다.
  if (status === 'checking') {
    return (
      <div className="admin-shell">
        <p className="admin-loading">확인 중...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-shell">
        <AdminLoginPanel
          onSubmit={login}
          isSubmitting={isSubmitting}
          errorMessage={error}
          onClearError={clearError}
        />
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <span className="admin-topbar-brand">새힘정보기술 관리자</span>
        <div className="admin-topbar-actions">
          <button type="button" className="admin-btn admin-btn-ghost" onClick={() => onNavigate('/')}>
            사이트로 이동
          </button>
          <button type="button" className="admin-btn admin-btn-ghost" onClick={logout} disabled={isSubmitting}>
            로그아웃
          </button>
        </div>
      </header>

      <nav className="admin-tabs" aria-label="관리 항목">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`admin-tab${activeTab === tab.id ? ' is-active' : ''}`}
            onClick={() => onNavigate(tab.path)}
            aria-current={activeTab === tab.id ? 'page' : undefined}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* [기술] 탭 전환 시 패널이 교체되며 편집 중이던 초안은 사라집니다.
                각 패널이 마운트 시점에 서버 데이터를 다시 불러오므로 화면과 서버가 어긋나지 않습니다. */}
      {activeTab === 'history' && <HistoryAdminPanel onUnauthorized={markUnauthorized} />}
      {activeTab === 'news' && <NewsAdminPanel onUnauthorized={markUnauthorized} />}
      {activeTab === 'faq' && <FaqAdminPanel onUnauthorized={markUnauthorized} />}
      {activeTab === 'content' && <ContentAdminPanel onUnauthorized={markUnauthorized} />}
    </div>
  );
}
